import {
  Box,
  Col,
  Icon,
  Loading,
  Row,
  Search,
  Text,
} from '@cubeyou-team/hx-react-components';
import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import uniqid from 'uniqid';

import { MODAL_TYPES } from 'src/components/RepositoryItemActions/constants';
import translations from 'src/lang';
import { useAudienceActivationContext } from 'src/modules/audience-activation/providers/AudienceActivationProvider';
import { SelectedDestinationItem } from 'src/modules/audience-activation/types';
import {
  DestinationDetailModal,
  MODAL_STATUS,
  ModalDetail,
} from './DestinationDetailModal';
import { DestinationsList } from './DestinationsList';
import { SelectedDestinationsList } from './SelectedDestinationsList';

const lang = 'en-US';

const StyledDestinationsContainer = styled.div`
  padding-bottom: 1.5rem;
  border-top: 1px solid #d8d7ea;
  border-bottom: 1px solid #d8d7ea;

  > div {
    min-height: 300px;
  }
`;

const StyledSelectedDestinationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.25rem;
  padding-bottom: 1rem;

  > p {
    margin-left: -0.5rem;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin: 0.75rem 0 0.5rem 0;
  padding: 0.5rem 0;
  border-top: 1px solid #d8d7ea;

  > div {
    width: 100%;

    > div:first-child {
      padding-left: 0;
    }
  }
`;

const StyledSearch = styled(Search)`
  border: none;

  &:focus {
    border: 0.125rem solid #1779cc;
  }
`;

export const DestinationsContainer = () => {
  const {
    modalType,
    destinations,
    isFetchingDestinations,
    selectedDestinations,
    setSelectedDestinations,
  } = useAudienceActivationContext();
  const [searchText, setSearchText] = useState<string>('');
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(true);
  const [
    destinationDetails,
    setDestinationDetails,
  ] = useState<SelectedDestinationItem | null>(null);
  const [modalDetail, setModalDetail] = useState<ModalDetail>({
    isOpen: false,
    status: MODAL_STATUS.NEW,
  });

  const filteredDestinations = useMemo(
    () =>
      [...destinations?.values()].filter(item =>
        item?.destination
          ?.toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase())
      ),
    [searchText, destinations]
  );

  const handleSearchChange = useCallback(
    (text: string) => setSearchText(text),
    [setSearchText]
  );

  const handleOnClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setIsAccordionOpen(prev => !prev);
    },
    [setIsAccordionOpen]
  );

  const handleCardClick = useCallback(
    (name: string) => {
      const destination = destinations.get(name);
      const item = ({
        ...destination,
        additional_properties: destination.additional_properties.map(prop =>
          typeof prop === 'string' ? { key: prop, value: '' } : prop
        ),
      } as unknown) as SelectedDestinationItem;

      setDestinationDetails({ ...item });
      setModalDetail({ isOpen: true, status: MODAL_STATUS.NEW });
    },
    [destinations, setDestinationDetails, setModalDetail]
  );

  const handleModalClose = useCallback(() => {
    setModalDetail({ isOpen: false, status: MODAL_STATUS.NEW });
    setDestinationDetails(null);
  }, [setModalDetail, setDestinationDetails]);

  const handleDetailsModalSuccess = useCallback(
    (additionalPropPayload: Array<{ key: string; value: string }>) => {
      const destination =
        modalDetail.status === MODAL_STATUS.EDIT
          ? selectedDestinations.get(destinationDetails.id)
          : destinations.get(destinationDetails.destination);

      const value = ({
        ...destination,
        isSelected: true,
        additional_properties: additionalPropPayload,
        id:
          modalDetail.status === MODAL_STATUS.EDIT
            ? destinationDetails.id
            : uniqid(),
      } as unknown) as SelectedDestinationItem;

      const newDestinations = new Map(selectedDestinations);
      newDestinations.set(value.id, value);

      setSelectedDestinations(newDestinations);
      handleModalClose();
    },
    [
      modalDetail,
      destinations,
      selectedDestinations,
      destinationDetails,
      setSelectedDestinations,
      handleModalClose,
    ]
  );

  const handleOnEdit = useCallback(
    (id: string) => {
      const item = selectedDestinations.get(id);

      setModalDetail({ isOpen: true, status: MODAL_STATUS.EDIT });
      setDestinationDetails(item);
    },
    [selectedDestinations, setDestinationDetails]
  );

  return (
    <StyledDestinationsContainer>
      <Box pT="small" pB="small">
        <Loading isLoading={isFetchingDestinations} size="medium">
          <StyledSelectedDestinationsContainer>
            <Text variant="dark_lighter" size="small" className="paragraph">
              {translations[lang].MODALS.AUDIENCE_ACTIVATION.DESTINATIONS_LABEL}
            </Text>
            <SelectedDestinationsList
              destinationDetails={destinationDetails}
              onEdit={handleOnEdit}
              setDestinationDetails={setDestinationDetails}
            />
          </StyledSelectedDestinationsContainer>
          {modalType !== MODAL_TYPES.OPEN_CAMPAIGN && (
            <StyledHeader>
              <Row valign="middle">
                <Col span="flex">
                  <StyledSearch
                    size="small"
                    placeholder={
                      translations[lang].MODALS.AUDIENCE_ACTIVATION
                        .DESTINATION_INPUT_PLACEHOLDER
                    }
                    initialValue={searchText}
                    onSearchChange={handleSearchChange}
                  />
                </Col>
                <Col span="auto">
                  {isAccordionOpen ? (
                    <Icon
                      icon="chevron_up"
                      size="xsmall"
                      onClick={handleOnClick}
                    />
                  ) : (
                    <Icon
                      icon="chevron_down"
                      size="xsmall"
                      onClick={handleOnClick}
                    />
                  )}
                </Col>
              </Row>
            </StyledHeader>
          )}
          {isAccordionOpen && modalType !== MODAL_TYPES.OPEN_CAMPAIGN && (
            <DestinationsList
              destinations={filteredDestinations}
              onClick={handleCardClick}
            />
          )}
        </Loading>
      </Box>
      <DestinationDetailModal
        modalDetail={modalDetail}
        destinationDetails={destinationDetails}
        onSuccess={handleDetailsModalSuccess}
        onClose={handleModalClose}
      />
    </StyledDestinationsContainer>
  );
};
